/**
 * <s-sticky-atc> is the phone-only fixed add-to-cart bar on product pages.
 * It shows (data-visible) once the product form's main button row ([data-atc-anchor]) has scrolled
 * above the viewport, and hides again near the bottom of the page so it never covers the footer.
 * The bar's button submits the real product form (form="…"), so all validation stays in place.
 * The element is re-created whenever the product-form partial re-renders on a variant change.
 */
class SStickyAtc extends HTMLElement {
  #ac = null;
  #observer = null;
  #pastAnchor = false;
  #frame = 0;

  connectedCallback() {
    const scope = this.closest('s-product-form') ?? this.parentElement;
    const anchor = scope?.querySelector('[data-atc-anchor]');
    if (!anchor) return;

    this.#ac = new AbortController();
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#pastAnchor = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.#update();
    });
    this.#observer.observe(anchor);

    window.addEventListener('scroll', () => this.#scheduleUpdate(), { passive: true, signal: this.#ac.signal });
    window.addEventListener('resize', () => this.#scheduleUpdate(), { passive: true, signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #scheduleUpdate() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const doc = document.documentElement;
    const nearBottom = window.scrollY + window.innerHeight >= doc.scrollHeight - 320;
    const visible = this.#pastAnchor && !nearBottom;
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
  }
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
